import { FC, useEffect, useRef, useState } from 'react';
import { SelectMenusProps } from './SelectMenu.types';
import {
  SelectMenusPage,
} from '../../ChangeSpecs/SelectMenus/SelectMenus.styles';
import { StyledFooter, StyledFooterText } from '../../../../components/Table/Table.styles';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import { StyledSubHeader } from '../../../Extract/Menu/SelectMenus/SelectMenus.styles';
import MenuTreeView from '../../../../components/MenuTreeView/MenuTreeView';
import {
  fetchMenusInOrganization, fetchOrganizations, useMenusCount,
  useMenusInOrg,
  useOrganizations,
} from '../../../../services/menu/menu.service';
import { Organization } from '../../../../services/menu/menu.types';
import useTreeViewRowStates from '../../../../components/MenuTreeView/useMenuTreeViewRowStates';
import isEqual from 'react-fast-compare';
import { showToast } from '../../../../components/Toast/Toast';
import { genericErrorShowToastOptions } from '../../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { ToastTypes } from '../../../../components/Toast/Toast.types';
import Radio from '../../../../components/Radio/Radio';
import { FiltersWrapper, HeaderInputButton, HeaderWrapper, StyledHeader, StyledHeaderTitle, StyledSubTitle, TableRowWrapper, TableWrapper } from './SelectMenu.styles';
import InputButton from '../../../../components/InputButton/InputButton';
import getDataStructure from '../../../../utils/dataStructure/dataStructure';
import Table from '../../../../components/Table/Table';
import { AgGridReact } from 'ag-grid-react';
import { GetRowIdParams, RowSelectedEvent } from 'ag-grid-community';

const SelectMenus: FC<SelectMenusProps> = ({
  disableLocalStorage = true,
  expandOnCheck = false,
  checkedMenus
  }) => {

  const [radioFilter, setRadioFilter] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [enableFilter, setEnableFilter] = useState<boolean>(false)
  let filter = {[radioFilter]: searchValue}
  const { data: orgs, isLoading: isOrgLoading } = useOrganizations({activateOrExtract: 'PENDING'}, true);
  const { data: menus } = useMenusInOrg({activateOrExtract: 'PENDING'}, true);
  const {data} = useMenusCount({activateOrExtract: 'PENDING', statusNo: 2 }, true);
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
  const [filterId, setFilterId] = useState<string>('');

  const availableOrgs = orgs?.data || [];
  const availableMenus = menus?.data || [];

  const [selectedMenus, setSelectedMenus] = useState<Organization[]>([]);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [tableUpdate, setTableUpdate] = useState<boolean>(false);

  const selectedGridRef = useRef<AgGridReact>(null);

  if (data && displayedRowCount === 0 && data.status === 206) {
    setDisplayedRowCount(parseInt(data.headers['x-total-count']));
  }

  const dataStructure = getDataStructure('PENDING');

  const getRowId = (params: GetRowIdParams) => {
    return params.data.id;
  };

  const { checkedRows, expandedRows, handleRowClick, handleCheckboxClick, handleUpwardsDeselectPropagation, resetCheckedRows, resetExpandedRows, handleExternalTableDeselectPropagation} = useTreeViewRowStates(
    dataStructure,
    'selectMenusToBulkUpdate',
    disableLocalStorage
  );

  const handleCheckboxClickInternal = (row: Organization, rowChecked: boolean) => {
    if (rowChecked) {
      if (row.type === "menu") {
        // menu row was checked
        setSelectedMenus((selectedMenu) => {
          const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
          const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
            const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
            return index === firstIndex;
          });
          return onlyUniqueValues.map((menu) => {
            return {
              ...menu
            };
          });
        });
      } else {
        if (row.type === "org" && !enableFilter) {
          fetchMenusInOrganization(
            { orgId: row.id, activateOrExtract: 'PENDING' },
            (response: { data: any; }) => {
              setSelectedMenus((selectedMenus) => {
                const selectedMenusWithResponseDataAdded = [...selectedMenus, ...response.data];
                const onlyUniqueValues = selectedMenusWithResponseDataAdded.filter((menu, index) => {
                  const firstIndex = selectedMenusWithResponseDataAdded.findIndex((val) => val.id === menu.id);
                  return index === firstIndex;
                });

                return onlyUniqueValues.map((org: Organization) => {
                  return {
                    ...org
                  };
                });
              });
            },
            () => {
              showToast({
                ...genericErrorShowToastOptions,
                label: 'Error fetching menus',
                toastType: ToastTypes.Error,
                id: 'errorFetchingMenusInOrgs',
              });
            }
          );
        } else {
          traverseTree(row);
        }
      }
    } else {
      // row was just unchecked
      setSelectedMenus((selectedMenus) => {
        // filter through selectedMenus and remove all the menus that correspond with that row
        return selectedMenus.filter((menu) => {
          let isRowAndParentEqual;
          if (menu.type === row.type) {
            isRowAndParentEqual = isEqual(row.id, menu.id) && isEqual(row.parents, menu.parents);
          } else {
            isRowAndParentEqual = isEqual(row.id, menu.parents ? menu.parents[row.type] : undefined);
          }
          return !isRowAndParentEqual;
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row, rowChecked);
  };

  const traverseTree = (row: Organization) => {
    if (row.type === 'menu') {
      setSelectedMenus((selectedMenu) => {
        const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
        const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
          const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
          return index === firstIndex;
        });
        return onlyUniqueValues.map((menu) => {
          return {
            ...menu,
            rowChecked: true
          };
        });
      });
      handleCheckboxClick(row, true);
    } else if (row.children !== undefined) {
      row.children.forEach(childRow => traverseTree(childRow))
    }
  }

  const handleSelectAll = () => {
    if (selectedMenus.length === displayedRowCount) {
      resetCheckedRows();
      setSelectedMenus([]);
    } else {
      availableMenus.forEach(menu => {
        handleCheckboxClick({
          type: 'org',
          id: menu.parents?.org,
          parents: null
        }, true);
        handleCheckboxClick(menu, true);
        setSelectedMenus(availableMenus);
      });
    }
  };

  useEffect(() => {
    if(checkedMenus){
      checkedMenus(selectedMenus)
    }
  }, [selectedMenus])

  const translateFilter: any = {
    marketNo : "org",
    storeNo : "store",
    menuNo : "menu"
  }

const handleSearch = async () => {
  await fetchOrganizations(
    { activateOrExtract: 'PENDING' },
    filter,
    radioFilter,
    (response: { data: Organization[] }) => {
      setFilteredOrganizations(response.data);
      if (response.data.length > 0) {
        const orgRow = response.data[0];
        switch (radioFilter) {
          case 'marketNo':
            setFilterId(response.data[0].id)
            break;
          case 'storeNo':
            setFilterId(searchValue);
            findSearchValue(searchValue, 'store', orgRow, orgRow);
            handleRowClick(orgRow, true);
            break;
          case 'menuNo':
            setFilterId(searchValue);
            findSearchValue(searchValue, 'menu', orgRow, orgRow);
            handleRowClick(orgRow, true);
            break;
        }
      }
    }
  );
  setEnableFilter(true);
}

const findSearchValue = (searchValue: string, type: string, row: Organization, orgRow: Organization) => {
  if (row.type === type && searchValue === row.id) {
    filterSiblings(row, orgRow, type);
  } else if (row.children) {
    row.children.forEach(childRow => findSearchValue(searchValue, type, childRow, orgRow))
  }
}

const filterSiblings = (targetRow: Organization, orgRow: Organization, type: string) => {
    orgRow.children?.forEach((row) => {
      if (row.type !== targetRow.type) {
        if (targetRow?.parents && targetRow.parents[row.type] === row.id) {
          handleRowClick(row, true);
          filterSiblings(targetRow, row, type);
        }
      }
    })
}


const handleClear = () => {
  setEnableFilter(false);
  setSearchValue('');
  setRadioFilter('');
  setFilterId('');
  resetExpandedRows();
  setFilteredOrganizations([]);
}
const handleRadioValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  setRadioFilter(event.target.value);
}
const handleSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
  setSearchValue(event.target.value)
}
  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      await handleSearch();
    }
  }

  const handleSelectedMenusChange = (event: RowSelectedEvent) => {
    if (event.node.isSelected() === false && event.rowIndex !== null) {
      const newSelectedMenus = selectedMenus.filter((menu) => menu.id !== event.data.id);
      setSelectedMenus(newSelectedMenus);
      handleCheckboxClick(event.data, false);
      handleExternalTableDeselectPropagation(event.data);
      selectedGridRef.current?.api.selectAll();
      setTableUpdate(!tableUpdate);
    }
  };

  useEffect(() => {
    selectedGridRef?.current?.api?.selectAll();
  }, [selectedMenus, selectedGridRef?.current?.api]);
  return (
    <SelectMenusPage>
      <TableRowWrapper>
          <TableWrapper>
            <StyledHeader data-testid='header'>
              <HeaderWrapper>
                <StyledHeaderTitle>Select pending menus</StyledHeaderTitle>
              </HeaderWrapper>
              <FiltersWrapper>
                <StyledSubTitle>Search by:</StyledSubTitle>
                <Radio checked={radioFilter === 'marketNo'} value='marketNo' onChange={handleRadioValueChange} id={'marketNo'} label={'Franchisee'} disabled={enableFilter} />
                <Radio checked={radioFilter === 'storeNo'} value='storeNo' onChange={handleRadioValueChange} id={'storeNo'} label={'Store'} disabled={enableFilter} />
                <Radio checked={radioFilter === 'menuNo'} value='menuNo' onChange={handleRadioValueChange} id={'menuNo'} label={'Menu'} disabled={enableFilter} />
                <HeaderInputButton>
                  <InputButton
                    buttonProps={{label: enableFilter ? 'Clear': 'Search', onClick: enableFilter ? handleClear : handleSearch, testId: 'SearchButton', disabled:  searchValue === ''}}
                    inputProps={{onChange: handleSearchValue, id: 'searchFilters', label: '', value: searchValue, disabled: enableFilter || radioFilter === '', onKeyDown: handleKeyDown, testId: 'FilterInput', maxLength: 20 }}
                  />
                </HeaderInputButton>
              </FiltersWrapper>
           </StyledHeader>
              <StyledSubHeader data-testid='subtitle'>
                <div style={{ marginLeft: '48px' }}>
                     <Checkbox
                      type='checkbox'
                      checked={selectedMenus.length > 0 && selectedMenus.length === displayedRowCount}
                      id={'select-all'}
                      aria-checked={selectedMenus.length > 0 && selectedMenus.length === displayedRowCount}
                      aria-disabled={enableFilter}
                      disabled={enableFilter}
                      readOnly
                      indeterminate={selectedMenus.length > 0 && selectedMenus.length < displayedRowCount}
                      label={<StyledSubTitle>Organizations</StyledSubTitle>}
                      onClick={handleSelectAll}
                    />
                  </div>
                </StyledSubHeader>
                <MenuTreeView
                rows={enableFilter ? filteredOrganizations : availableOrgs}
                dataStructure={dataStructure}
                checkedRows={checkedRows}
                expandOnCheck={expandOnCheck}
                expandedRows={expandedRows}
                selectedRows={selectedMenus}
                onRowClick={handleRowClick}
                onCheckboxClick={handleCheckboxClickInternal}
                defaultHandleCheckboxClick={handleCheckboxClick}
                isLoading={isOrgLoading}
                radioFilter={translateFilter[radioFilter]}
                filterId={filterId}
                externalTableUpdate={tableUpdate}
                height={'calc(100vh - 330px)'}
                />
                 <StyledFooter>
                <StyledFooterText>
                {`${displayedRowCount === undefined ? 0 : displayedRowCount} ${displayedRowCount === 1 ? 'record' : 'records'}`}
                </StyledFooterText>
              </StyledFooter>
          </TableWrapper>
          <TableWrapper>
            <Table
              showHeader
              height={'calc(100vh - 160px)'}
              headerTitle='Selected'
              columnDefs={
                 [
                  {
                    headerName: 'Select',
                    checkboxSelection: true,
                    minWidth: 70,
                    width: 70,
                    floatingFilter: false,
                  },
                  { headerName: 'Store No', field: 'parents.store', flex: 1, resizable: false, minWidth: 100 },
                  { headerName: 'Menu ID', field: 'id', width: 300 },
                ]
              }
              onRowSelected={handleSelectedMenusChange}
              rowData={selectedMenus}
              onFirstDataRendered={({api}) => api.forEachNode(node => node.setSelected(true))}
              returnedRecords={selectedMenus.length}
              ref={selectedGridRef}
              rowSelection='multiple'
              suppressRowClickSelection={true}
              getRowId={getRowId}
            />
          </TableWrapper>
      </TableRowWrapper> 
    </SelectMenusPage>
  )

};

export default SelectMenus;